import React from "react";

import { useParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import useEditReport from "@app/analytics/MeetingReport/hooks/useEditReport";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import { toast } from "@app/common/hooks/useToasts";

import ConfirmationModal from "@rui/ConfirmationModal";
import InfoPanel from "@rui/InfoPanel";

import RecapSettings from "./RecapSettings";
import TranscriptFilters from "./TranscriptFilters";

const RestoreDialogs = () => {
  const { sessionId } = useParams() as { sessionId: string };

  const {
    canRestoreNotes,
    canRestoreTranscript,
    restoreTranscript,
    restoreNotes,
  } = useEditReport();

  const { editAccessLevel, modal, setModal } = useEditStore((state) => ({
    editAccessLevel: state.editAccessLevel,
    modal: state.modal,
    setModal: state.setModal,
  }));

  const canRestore =
    editAccessLevel === "full" && (canRestoreNotes || canRestoreTranscript);

  if (!canRestore) return null;

  const handleRestoreNotes = () => {
    restoreNotes.mutate(sessionId, {
      onError: () => {
        toast.error(
          "Something went wrong and the notes could not be reverted. Please try again or reach out to support@read.ai if the problem persists."
        );
      },
    });
  };

  const handleRestoreTranscript = () => {
    restoreTranscript.mutate(sessionId, {
      onError: () => {
        toast.error(
          "Something went wrong and the transcript could not be reverted. Please try again or reach out to support@read.ai if the problem persists."
        );
      },
    });
  };

  const closeModal = () => setModal(null);

  return (
    <>
      {canRestoreTranscript && (
        <ConfirmationModal
          open={modal === "confirm_revert_transcript"}
          onClose={closeModal}
          onConfirm={handleRestoreTranscript}
          title="Revert all changes?"
          size="md"
          confirmProps={{
            label: "Yes, revert changes",
            type: "negative",
          }}
          loading={restoreTranscript.isPending}
        >
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Reverting the transcript to its original state will reverse any
            changes made, including updates to speaker names. Are you sure you
            want to continue?
          </Typography>
          <InfoPanel severity="info">
            Only report owners and editors can make and revert changes to the
            report.
          </InfoPanel>
        </ConfirmationModal>
      )}
      {canRestoreNotes && (
        <ConfirmationModal
          open={modal === "confirm_revert_notes"}
          onClose={closeModal}
          onConfirm={handleRestoreNotes}
          title="Revert all changes?"
          size="md"
          confirmProps={{
            label: "Yes, revert changes",
            type: "negative",
          }}
          loading={restoreNotes.isPending}
        >
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            When you revert the notes to their original state, you will reverse
            any changes made. Are you sure you want to continue?
          </Typography>
          <InfoPanel severity="info">
            Only report owners and editors can make and revert changes to the
            report.
          </InfoPanel>
        </ConfirmationModal>
      )}
    </>
  );
};

const MeetingReportRecapDialogs = () => {
  const { inMobileView } = useResponsiveView();

  return (
    <>
      <RestoreDialogs />
      {inMobileView && (
        <>
          <RecapSettings variant="dialog" />
          <TranscriptFilters variant="dialog" />
        </>
      )}
    </>
  );
};

export default MeetingReportRecapDialogs;
